import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, Popup, ZoomControl, Pane } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

import { useTheme } from '../../../context/ThemeContext';
import { useUser } from '../../../context/UserContext';
import { useMultiCommodityRegionsData } from '../../../utils/api-hooks/analytics';
import { useEventGeometry } from '../../../utils/api-hooks/eventGeometry';
import { useWeatherEvents } from '../../../utils/api-hooks/weather';

import ShipMarker, { voyageHasDelay, voyageIsAhead, currentVesselName } from '../tracking/ShipMarker';
import { isHubWaiting, hubFocusInStack, buildHubWaitingPanel, selectHubPanelMembers, resolvePinHit, resolveSearchHit, resolveSelectedProp, applySelectionDecision } from '../../../utils/hubWait';
import HubWaitingPanel from '../tracking/HubWaitingPanel';
import { groupPinStacks } from '../../../utils/pinStacks';
import RouteLayer from '../tracking/RouteLayer';
import { shipmentStatusKey } from '../../../utils/shipmentChipFilters';
import { STATUS_COLORS as SHIPMENT_STATUS_COLORS } from './modeIcons';
import ModeShipmentLayer, { buildModeItems } from './ModeShipmentLayer';
import { modeRowKey, isSeaRow } from '../../../utils/modeRows';
import { MapResizeHandler, MapClickHandler, ShipClickResolver, ShipMarkerPanes, DisableZoomHandlers, RegionZoomHandler, FocusRegionHandler, FocusShipmentHandler, MapCenterHandler, MapViewportHandler, ZoomToClusterHandler, ResetViewHandler,
  RegionHoverLock,
} from './MapHelpers';
import { injectLeafletStyles } from './leafletStyles';
import {
  STATUS_COLORS,
  OPENWEATHERMAP_API_KEY,
  API_MODE,
  computeForecastMaxHours,
  regionMarkerId,
} from './weatherConstants';
import AnimatedWeatherLayer from './AnimatedWeatherLayer';
import HazardGeometryLayer, { FitHazardOnReady } from './HazardGeometryLayer';
import HazardCard from './HazardCard';
import HazardEventMarker from './HazardEventMarker';
import { useMapInsets } from './useMapInsets';
import { useHazardSelection } from './useHazardSelection';
import { hazardBounds } from '../../../utils/gdacs/hazardBounds';
import { shiftedStormBounds, regionPinPosition } from '../../../utils/gdacs/regionPinFrame';
import { pathUpdatedMsOf } from '../../../utils/gdacs/geometryQuery';
import { eventListRefetchInterval, shouldRevalidateEvents } from '../../../utils/gdacs/eventListPolicy';
import { hazardBiqSummary } from '../../../utils/gdacs/hazardBiqSummary';
import { resolveStormClass } from '../../../utils/gdacs/stormClass';
import { useBiqPageContext } from '../../../context/BiqPageContext';
import { timelineEpochMs, stormStateAt, staticStormState } from '../../../utils/gdacs/stormClock';
import { CARTO_LIGHT_URL, CARTO_DARK_URL } from '../../../constants/URLs';
import { RegionPopupContent } from './WeatherPopups';
import WeatherLayerLegend from './WeatherLayerLegend';
import StatusLegend from './StatusLegend';
import RegionStatusLegend from './RegionStatusLegend';
import LegendCollapse from './LegendCollapse';
import MapLegend from './MapLegend';
import MapModeStatusLegend from './MapModeStatusLegend';
import WeatherMapControls, { STATUS_OPTIONS, WEATHER_LAYERS, EventsDropdown, StatusDropdown } from '../home/WeatherMapControls';

import { LAYER_MODES } from '../../../utils/mapUrlParams';
import WeatherMapTimeline from '../home/WeatherMapTimeline';
import LayerToggle from './LayerToggle';
import MapLayerRail from './MapLayerRail';
import { layersForMode } from '../../../utils/mapViews';
import MapSelect from './MapSelect';
import '../tracking/trackingStyles.css';

import { Loader2 } from 'lucide-react';
import { createLeafletPinIcon } from './leafletPinIcon';
import { createGrowingPinIcon, createGrowingClusterIcon, GROWING_PIN_WIDTH, GROWING_CLUSTER_WIDTH } from './growingPinIcon';
import { groupRegionsByArea } from './growingClusters';
import { getHazardIcon } from './hazardIcon';
import { shipmentPinLatLng, pinStackKey, offsetDelayedFromNeighbors } from '../tracking/portCoords';
import { predictedEta, timelinePct } from '../../../utils/shipmentSchedule';

const ALL_STATUS_IDS = STATUS_OPTIONS.map((s) => s.id);
/** Pins within this distance (km) share one click and open the top-left list. ~0.018°. */
const STACK_OPEN_KM = 2;

// Default predicate; module-level so its identity is stable across renders.
const PASS = () => true;
// Stable default array identity.
const NO_COMMODITIES = [];


/**
 * The map's timeline offers normal and double speed.
 *
 * Half speed was dropped in review: the slowest option existed for tiles to
 * keep up, and the pair that is actually useful when scrubbing a forecast is
 * "play it" and "play it faster". The 0.5x entry still exists in the
 * timeline's own SPEED_OPTIONS — Home's strip shows all three — this list is
 * only which of them the map surfaces.
 */
const TIMELINE_SPEEDS = ['1x', '2x'];

/** Hovering a region's card in the side panel grows its pin by this much. */
const REGION_PIN_HOVER_SCALE = 1.3;

/** Stable identity so the default prop does not retrigger memos. */
const EMPTY_EXPANDED = new Set();

/**
 * How much of the map a popup must keep clear of, in CSS px.
 *
 * The overlay chrome — filter strip, chip row, Alerts and Arrivals pills —
 * floats over the map's top-left in MapPage's column, which sits OUTSIDE this
 * component's `z-0 isolate` wrapper. Nothing rendered inside the map can paint
 * above it, so a popup opening under that corner is simply covered: the region
 * card's "Status" line disappeared behind the weather chips.
 *
 * Leaflet already pans a popup into view; it just does it with a 5px margin
 * and no idea the chrome is there. Widening the padding is what moves the
 * popup clear. The top figure is the column's stack at its tallest (24px
 * inset, strip, chips, then the Alerts and Arrivals pills with 12px gaps);
 * the right and bottom clear the layer rail and the forecast timeline.
 *
 * Deliberately constants rather than measured: the column spans nearly the
 * map's full height, so `useMapInsets` would report almost all of it as
 * covered. Only the stack at the top actually obscures anything.
 */
const POPUP_PAN_TOP_LEFT = [16, 200];
const POPUP_PAN_BOTTOM_RIGHT = [88, 112];

/**
 * Where the map keys sit, relative to the weather timeline.
 *
 * The timeline row is centred and capped at `min(900px, 100% - 360px)`, so on
 * desktop it leaves a margin the keys can sit in — the design puts the two on
 * one bottom line. What decides whether that margin is really free is the
 * 320px temperature scale, which rides the same row on Weather and is dropped
 * on All:
 *
 *   - All: the row is the timeline alone, so the cap hands back a fixed ~180px
 *     each side from lg. The keys need ~124px, so they keep the corner and
 *     share the timeline's line.
 *   - Weather: timeline + gap + scale comes to `100% - 28px`, leaving ~14px,
 *     so the keys have to clear the row at every width. The 900px cap does
 *     open a margin from 2xl, but not a usable one: the row centres, so the
 *     card starts ~152px in while the key is anchored at left-[24px] and runs
 *     wider than the ~124px budgeted here. Dropping the key back onto the
 *     timeline's line there put it BEHIND the card (z-900 under z-1040), which
 *     also swallowed every click below the pill's top few pixels.
 *
 * Below lg the timeline is full-bleed (`max-lg:inset-x-0`) on both, so nothing
 * shares its line and the keys always clear it: 88px of timeline plus a 16px
 * gap. Docked on desktop it is 64px, giving the 80px lift.
 */
const legendBottomFor = ({ hasTimeline, hasScale }) => {
  if (!hasTimeline) return 'bottom-[16px] lg:bottom-[24px]';
  // Weather carries 16px more than the geometry alone asks for: the timeline
  // card's shadow and its rounded top corner ate into the nominal gap, so the
  // key sat visually on top of the scrubber even where the numbers said it
  // cleared. All three steps move together to keep the gap consistent.
  //
  // The phone figure also clears the temperature scale, which stacks ABOVE the
  // timeline below lg (88px timeline + 8px gap + the scale + the 16px above):
  // from lg the scale sits beside the timeline instead and costs no height.
  return hasScale
    ? 'bottom-[136px] lg:bottom-[96px]'
    : 'bottom-[104px] lg:bottom-[24px]';
};

/**
 * A route's colour, matching the marker it belongs to.
 *
 * `at_port` carries no colour on purpose — ShipMarker draws those as the box
 * glyph and air/truck rows fall back to on-time — so a bare lookup returns
 * undefined, and RouteLayer then falls back to ITS own green. That put a green
 * line under a blue marker and read as "this ship is ahead" when it was simply
 * waiting. Falling back to on_time here applies the marker's rule to the route.
 */
const routeColorFor = (row) =>
  SHIPMENT_STATUS_COLORS[shipmentStatusKey(row)] || SHIPMENT_STATUS_COLORS.on_time;

const UnifiedMapContainer = ({
  shipments = [],
  flights = [],
  trucks = [],
  layerMode = LAYER_MODES.ALL,
  onLayerModeChange,
  selectedShipment: selectedShipmentProp,
  onShipmentSelect,
  commodities = NO_COMMODITIES,
  selectedCommodity = '',
  selectedStatuses = [],
  onStatusesChange = () => {},
  zoomCountry = '',
  zoomRegion = '',
  hoveredRegionId = null,
  focusedRegion = null,
  mobileControlsExpanded = false,
  // Cluster selection lives in MapPage so its overview can render in the
  // page's overlay column, the same way the shipment panel does.
  selectedCluster = null,
  onClusterSelect = () => {},
  // Set to a cluster to fit the map around its regions; cleared by the page.
  zoomToCluster = null,
  // Region ids belonging to clusters the user chose to open in place. Ids
  // rather than cluster keys: a cluster's key encodes its membership, so it
  // changes the moment a zoom regroups the pins, and an expansion stored
  // against it would vanish silently.
  expandedRegionIds = EMPTY_EXPANDED,
  resetViewSeq = 0,
  seaOn = true,
  countryPredicate = PASS,
  // ContainerSearch now lives in the persistent filter strip (MapPage), so
  // this arrives as a prop instead of being owned here.
  containerDetailOpen = false,
  // The weather tile layer and the hazard types are owned by MapPage, for the
  // same reason cluster selection is: their controls render in the page's
  // overlay column, under the filter strip, not inside the map.
  activeLayer = 'temp',
  onLayerChange = () => {},
  activeEventTypes = [],
  onEventTypeToggle = () => {},
  // Saved views. `activeLayers` is the set actually drawn — derived in
  // MapPage from either a live view or the layer mode — so this component
  // never has to know which of the two is in charge.
  activeLayers = null,
  savedViews = [],
  activeViewId = null,
  onSelectView = () => {},
  onCombineSave = () => {},
  onUpdateView = () => {},
  onDeleteView = () => {},
}) => {
  const { isDark } = useTheme();
  const { email } = useUser();
  const { t } = useTranslation();

  // Driven by the commodity selector: when a commodity is picked, fetch & show
  // regions for that one commodity. Otherwise fall back to the user's preferred
  // commodities (initial state on first visit, before any selection).
  const visibleCommodities = useMemo(
    () => (selectedCommodity ? [selectedCommodity] : commodities),
    [selectedCommodity, commodities],
  );

  // Derived from URL: when no status param, default to all (no silent filtering).
  const visibleStatuses = useMemo(
    () => (selectedStatuses && selectedStatuses.length > 0 ? selectedStatuses : ALL_STATUS_IDS),
    [selectedStatuses],
  );
  const [currentHour, setCurrentHour] = useState(0);
  // Bumped by every user scrub (slider drag / marker click), never by the play interval, so the storm glyph snaps.
  const [scrubRevision, setScrubRevision] = useState(0);
  const handleScrub = useCallback((hour) => {
    setScrubRevision((r) => r + 1);
    setCurrentHour(hour);
  }, []);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPausedForZoom, setIsPausedForZoom] = useState(false);
  const [isLayerLoading, setIsLayerLoading] = useState(false);
  const [playSpeed, setPlaySpeed] = useState(1500);
  const [stackPick, setStackPick] = useState(null);
  // Open "Waiting at {port}" panel: stable identity only (hub key, pin anchor, optional focused trip).
  const [hubPanel, setHubPanel] = useState(null);
  const [searchClearSeq, setSearchClearSeq] = useState(0);
  const [hoveredStackId, setHoveredStackId] = useState(null);
  const [hoveredShipment, setHoveredShipment] = useState(null);
  // Plane/truck under the cursor, resolved geometrically by ShipClickResolver.
  const [hoverModeKey, setHoverModeKey] = useState(null);

  // Derived, never stored. All three modes share one selection now, so holding
  // the air/truck key in its own state gave the map two sources of truth that
  // could disagree — selecting a vessel after a plane left the plane's arc and
  // its focus ring on the map, because only one of the two was cleared.
  const selectedModeKey = useMemo(
    () => (selectedShipmentProp && !isSeaRow(selectedShipmentProp) ? modeRowKey(selectedShipmentProp) : null),
    [selectedShipmentProp],
  );
  // Air/truck rows open the same ShipmentDetailsPanel as vessels, so selecting
  // one is just setting the page's selection; the key above follows from it.
  const selectModeRow = useCallback((row) => onShipmentSelect?.(row), [onShipmentSelect]);
  const airItems = useMemo(() => buildModeItems(flights, 'air'), [flights]);
  const truckItems = useMemo(() => buildModeItems(trucks, 'truck'), [trucks]);
  // An unchecked mode loses its hover (MapPage drops the selection itself).
  useEffect(() => {
    if (hoverModeKey && !airItems.some((i) => i.key === hoverModeKey) && !truckItems.some((i) => i.key === hoverModeKey)) {
      setHoverModeKey(null);
    }
  }, [airItems, truckItems, hoverModeKey]);
  // The arc draws for `hoverKey || selectedKey`, so a stale hover would keep it
  // alive even once the selection moved to a vessel.
  useEffect(() => {
    if (selectedShipmentProp && isSeaRow(selectedShipmentProp)) setHoverModeKey(null);
  }, [selectedShipmentProp]);
  const modeHoverRows = useMemo(
    () => [...airItems, ...truckItems].map((i) => ({ key: i.key, position: i.pos, selected: i.key === selectedModeKey })),
    [airItems, truckItems, selectedModeKey],
  );
  // Hour 0 of the timeline is mount time.
  const [startDate] = useState(() => new Date());
  // The OpenWeather forecast end is anchored to a daily refresh, NOT to mount
  // time. We compute the actual playable horizon once at mount so the timeline
  // and play wrap stop precisely at the last bucket with real tile data.
  const [maxHours] = useState(() => computeForecastMaxHours(startDate));
  const playIntervalRef = useRef(null);
  const wasPlayingBeforeZoomRef = useRef(false);

  // Fetch weather data — driven by the commodity selector. When a commodity is
  // picked, fetch only that one's regions; otherwise fall back to all preferred.
  const { regions, isLoading: regionsLoading } = useMultiCommodityRegionsData(
    email,
    visibleCommodities,
    null,
  );
  // The refresh interval depends on the selected storm, which only exists further down: it goes through state.
  const [eventsRefetchMs, setEventsRefetchMs] = useState(false);
  const { events, refetch: refetchEvents, dataUpdatedAt: eventsUpdatedAt } = useWeatherEvents({ refetchInterval: eventsRefetchMs });

  // Inject Leaflet styles
  useEffect(() => {
    return injectLeafletStyles(isDark, 'leaflet-custom-styles-unified-map');
  }, [isDark]);

  // Weather animation controls
  const handlePlayPause = useCallback(() => {
    setIsPlaying(prev => !prev);
    setIsPausedForZoom(false);
  }, []);

  const handleZoomStart = useCallback(() => {
    if (isPlaying) {
      wasPlayingBeforeZoomRef.current = true;
      setIsPausedForZoom(true);
    }
  }, [isPlaying]);

  const handleTilesLoaded = useCallback(() => {
    if (wasPlayingBeforeZoomRef.current && isPausedForZoom) {
      setIsPausedForZoom(false);
      wasPlayingBeforeZoomRef.current = false;
    }
  }, [isPausedForZoom]);

  const handleLayerLoading = useCallback(() => setIsLayerLoading(true), []);
  const handleLayerReady = useCallback(() => setIsLayerLoading(false), []);

  // Play interval. Wrap at `maxHours` (the last bucket the API actually has
  // tiles for, derived from the daily OWM refresh boundary). Snapping back to
  // 0 keeps playback on the 3-hour bucket grid and reuses the already-cached
  // hour-0 tile from the previous loop, eliminating the visible layer-less
  // gap that mod-241 + a hardcoded 240 cap used to produce.
  useEffect(() => {
    const shouldAnimate = isPlaying && !isPausedForZoom;
    if (shouldAnimate) {
      playIntervalRef.current = setInterval(() => {
        setCurrentHour((prev) => {
          const step = API_MODE === 'paid' ? 3 : 1;
          const next = prev + step;
          return next > maxHours ? 0 : next;
        });
      }, playSpeed);
    } else {
      if (playIntervalRef.current) {
        clearInterval(playIntervalRef.current);
        playIntervalRef.current = null;
      }
    }
    return () => { if (playIntervalRef.current) clearInterval(playIntervalRef.current); };
  }, [isPlaying, isPausedForZoom, playSpeed, maxHours]);

  const pinRegions = regions;

  // Filter regions. Commodity comparison is case-insensitive: NestedCommoditySelector
  // emits lowercase, but `r.commodityKey` may inherit the casing from preferred_commodities.
  const filteredRegions = useMemo(() => {
    if (visibleCommodities.length === 0 || visibleStatuses.length === 0) return [];
    const allowedCommodities = new Set(
      visibleCommodities.map((c) => (c || '').toLowerCase()),
    );
    return pinRegions.filter(
      (r) =>
        allowedCommodities.has((r.commodityKey || '').toLowerCase()) &&
        visibleStatuses.includes(r.harvest_status) &&
        r.latitude != null &&
        r.longitude != null,
    );
  }, [pinRegions, visibleCommodities, visibleStatuses]);

  // Filter events
  const filteredEvents = useMemo(() => {
    if (activeEventTypes.length === 0) return [];
    return events.filter(e => activeEventTypes.includes(e.type));
  }, [events, activeEventTypes]);

  // The live map, in a ref: storing it in state would re-render on every pan.
  // It no longer drives the region grouping — that is computed from the data —
  // but other handlers still need the instance.
  const mapRef = useRef(null);
  // The legend's readout samples weather at the middle of what is on screen,
  // so it follows the map rather than a fixed point.
  const [viewCenter, setViewCenter] = useState(null);
  const handleViewportChange = useCallback((map) => {
    setViewCenter(map.getCenter());
    mapRef.current = map;
  }, []);
  /** The selected cluster is owned by MapPage, so its overview can sit in the
   *  page's overlay column beside the filters rather than inside the map. */
  const openCluster = selectedCluster;
  const setOpenCluster = onClusterSelect;

  const regionPinIconCache = useMemo(() => new Map(), []);
  const clusterIconCache = useMemo(() => new Map(), []);
  // Marker instances keyed by regionMarkerId — lets a clicked Harvest Alerts card open the popup.
  const regionMarkerRefs = useRef(new Map());

  const getRegionPinIcon = useCallback((status, muted, enlarged = false) => {
    // Growing is the regions' own layer, so pins get the design's full 43px
    // there. On All they share the map with vessels, planes and trucks, so they
    // step back to a smaller, dimmed pin rather than crowding it.
    // One size on every layer; `muted` dims, it does not shrink.
    const base = GROWING_PIN_WIDTH;
    const sizePx = enlarged ? Math.round(base * REGION_PIN_HOVER_SCALE) : base;
    // `isDark` is part of the key, not only the call. These icons are cached
    // for the life of the map, so keying without it would keep serving the
    // light-mode pins after a theme switch — the artwork is built once.
    const key = `region|${status}|${sizePx}|${muted ? 'muted' : ''}|${isDark ? 'dark' : 'light'}`;
    const cached = regionPinIconCache.get(key);
    if (cached) return cached;
    const icon = createGrowingPinIcon({ status, sizePx, muted, isDark });
    regionPinIconCache.set(key, icon);
    return icon;
  }, [regionPinIconCache, isDark]);

  // Clusters follow single pins across layers: same size everywhere, dimmed on
  // All so regions sit behind the shipments and weather they share it with.
  const clusterMuted = layerMode === LAYER_MODES.ALL;
  const clusterWidth = GROWING_CLUSTER_WIDTH;

  // Keyed on the whole status breakdown, not just the total: two clusters of
  // the same size can carry different mixes, and the ring draws the mix.
  const getClusterIcon = useCallback((counts, count) => {
    const mix = Object.keys(counts).sort().map((s) => `${s}:${counts[s]}`).join(',');
    const key = `cluster|${mix}|${count}|${clusterWidth}|${clusterMuted ? 'muted' : ''}|${isDark ? 'dark' : 'light'}`;
    const cached = clusterIconCache.get(key);
    if (cached) return cached;
    const icon = createGrowingClusterIcon({
      counts, count, sizePx: clusterWidth, muted: clusterMuted, isDark,
    });
    clusterIconCache.set(key, icon);
    return icon;
  }, [clusterIconCache, clusterWidth, clusterMuted, isDark]);

  // Locations grouped by the region they belong to in the warehouse. Unlike
  // the pixel clustering this replaced, the grouping does not depend on the
  // view: it needs no map instance and no redraw when the camera settles, and
  // panning or zooming cannot change it. Zoom no longer separates a group
  // either — that is what the overview's "Expand" is for.
  const regionClusters = useMemo(
    () => groupRegionsByArea(filteredRegions),
    [filteredRegions],
  );

  // NOTE: clearing a stale selection is MapPage's job, keyed on the filter
  // values themselves. It cannot be done here against `filteredRegions`: that
  // array has a new identity on every render (it derives from `regions`, whose
  // memo depends on the array `useQueries` rebuilds each time), so an effect
  // watching it fires constantly — and cleared the selection on the very
  // render the click had just set it, closing the overview as it opened.


  // A country-filtered-out selection keeps its card open but gets no route,
  // port pins or fly-to.
  // Sea off: no selected vessel, card, route or re-inserted marker, even on the first paint.
  // Air/truck rows share the details panel but none of the sea pipeline, so
  // every consumer below (vessel marker, voyage route, fly-to) sees null for
  // them — otherwise selecting a plane re-inserted it into the vessel layer.
  const selectedShipment = seaOn && isSeaRow(selectedShipmentProp) ? selectedShipmentProp : null;
  const visibleSelected = selectedShipment && countryPredicate(selectedShipment) ? selectedShipment : null;

  const markerShipments = useMemo(() => {
    const list = [...(shipments || [])];
    const sel = selectedShipment?.container_number;
    if (sel) {
      const id = String(sel).toUpperCase();
      if (
        !list.some((s) => String(s?.container_number || '').toUpperCase() === id)
        && countryPredicate(selectedShipment)
      ) {
        list.push(selectedShipment);
      }
    }
    return list;
  }, [shipments, selectedShipment, countryPredicate]);

  const stackedMarkers = useMemo(() => {
    const merged = new Map(groupPinStacks(markerShipments, shipmentPinLatLng, pinStackKey, STACK_OPEN_KM)
      .map(({ root, items }) => [`m:${root}`, items]));
    const out = [];
    merged.forEach((items, key) => {
      const delayed = items.filter((item) => voyageHasDelay(item.shipment));
      const ahead = items.filter((item) => !voyageHasDelay(item.shipment) && voyageIsAhead(item.shipment));
      const rest = items.filter((item) => !voyageHasDelay(item.shipment) && !voyageIsAhead(item.shipment));
      const ranked = [...delayed, ...ahead, ...rest];
      const representative = ranked[0];
      const under = null; // one map icon per stack; side list selects among members
      out.push({
        ...representative,
        key,
        stackSize: items.length,
        stack: items,
        under,
        position: representative.pin, // true AIS only — no spiderfy/nudge for stacks
      });
    });
    out.forEach((row) => {
      if (!voyageHasDelay(row.shipment)) return;
      const neighbors = out.filter((other) => other.key !== row.key).map((other) => other.pin);
      const pushed = offsetDelayedFromNeighbors(row.pin, neighbors);
      if (pushed !== row.pin) row.position = pushed;
    });
    return out;
  }, [markerShipments]);

  const handleShipHover = useCallback((row) => {
    setHoveredShipment(row?.shipment || null);
  }, []);

  const hoverPreviewShipment = useMemo(() => {
    const s = seaOn ? hoveredShipment : null;
    if (!s) return null;
    if (!(s.route_geojson || s.voyage_stops)) return null;
    if (!countryPredicate(s)) return null;
    const selectedId = String(visibleSelected?.container_number || '').trim();
    if (selectedId && String(s.container_number || '').trim() === selectedId) return null;
    return s;
  }, [hoveredShipment, visibleSelected, countryPredicate, seaOn]);

  // Stack-picker rows the Origin/Destination filter hides are dropped during
  // render; the picker is hidden below two remaining entries.
  const visibleStackPick = useMemo(() => {
    if (!stackPick || !seaOn) return null;
    const kept = stackPick.filter((item) => countryPredicate(item.shipment));
    return kept.length > 1 ? kept : null;
  }, [stackPick, countryPredicate, seaOn]);
  // Shipments arrive already filtered by commodity from MapPage (single source of truth).
  const showShipments = (Array.isArray(activeLayers) ? activeLayers : layersForMode(layerMode))
    .includes(LAYER_MODES.SHIPMENTS);
  // Panel members come from the CURRENT filtered list (country filters, Sea, layer), never from
  // stale stack objects or markerShipments.
  const hubMembers = useMemo(() => {
    if (!hubPanel || !seaOn || !showShipments) return null;
    return selectHubPanelMembers({
      shipments,
      hubKey: hubPanel.hubKey,
      predicate: countryPredicate,
      pinLatLng: shipmentPinLatLng,
      cellKey: pinStackKey,
      km: STACK_OPEN_KM,
    });
  }, [hubPanel, shipments, countryPredicate, seaOn, showShipments]);
  const hubPanelModel = useMemo(
    () => (hubMembers && hubMembers.hubCards.length ? buildHubWaitingPanel(hubMembers.hubCards, hubMembers.others, hubMembers.otherHubCards) : null),
    [hubMembers],
  );
  // One place applies a selection transition (see applySelectionDecision); the card is the parent's prop.
  const commit = useCallback((decision) => {
    if (decision.kind === 'none') return; // writing the render's stale state would undo a queued setHubPanel(null)
    const next = applySelectionDecision(
      { hubPanel, stackPick, hoveredStackId, selectedShipment: selectedShipmentProp }, decision,
    );
    setHubPanel(next.hubPanel);
    setStackPick(next.stackPick);
    setHoveredStackId(next.hoveredStackId);
    if (next.clearSearch) setSearchClearSeq((n) => n + 1);
    if (next.selectedShipment !== selectedShipmentProp) onShipmentSelect(next.selectedShipment);
  }, [hubPanel, stackPick, hoveredStackId, selectedShipmentProp, onShipmentSelect]);
  // Filters removed every hub box: close the panel.
  useEffect(() => {
    if (!hubPanel || hubPanelModel) return;
    setHubPanel(null);
  }, [hubPanel, hubPanelModel]);
  const closeHubPanel = useCallback(() => setHubPanel(null), []);
  // Upstream this turned a hub selection into the waiting panel. That panel is
  // not part of this layout, and the conversion also cleared `selectedShipment`
  // — so it undid a hub click on the very next render. Only the useful half
  // is kept: a hub the filters have removed stops being selected.
  useEffect(() => {
    const sel = selectedShipmentProp;
    if (!sel || !isHubWaiting(sel)) return;
    const live = seaOn && showShipments
      && (shipments || []).some((s) => isHubWaiting(s) && countryPredicate(s)
        && String(s.container_number || '') === String(sel.container_number || ''));
    if (!live) commit({ kind: 'clear' });
  }, [selectedShipmentProp, shipments, countryPredicate, seaOn, showShipments, commit]);
  // A stale picker must not survive Sea being unchecked (nor resurrect a ship later).
  useEffect(() => {
    if (!seaOn) { setStackPick(null); setHoveredStackId(null); setHoveredShipment(null); }
  }, [seaOn]);


  // What is on the map, from one list. `activeLayers` is a saved view's set
  // when one is live, otherwise the set the layer mode implies — including
  // Weather keeping region pins, since the forecast is about the places that
  // grow the crop and tiles with no pins answer that for nobody. A view says
  // exactly what was ticked and nothing else, which is the point of combining.
  const drawn = Array.isArray(activeLayers) ? activeLayers : layersForMode(layerMode);
  const showWeather = drawn.includes(LAYER_MODES.WEATHER);
  const showRegions = drawn.includes(LAYER_MODES.GROWING);
  const weatherOpacity = 0.8;
  const hasApiKey = !!OPENWEATHERMAP_API_KEY;

  // The timeline row renders on any layer carrying weather; the temperature
  // scale beside it only on Weather itself (see the row below).
  const legendBottomClass = legendBottomFor({
    hasTimeline: showWeather && hasApiKey,
    hasScale: showWeather && hasApiKey && layerMode === LAYER_MODES.WEATHER,
  });

  // Hazard detail: one selected event, its lazily fetched footprint, the pins inside it and the
  // storm position for the shared timeline clock.
  const { selectedEvent, selectEvent, clearSelection } = useHazardSelection({
    events,
    activeEventTypes,
    enabled: showWeather,
  });
  // Event list: revalidated when a live cyclone is opened (if older than 5 min) and polled while one stays selected.
  useEffect(() => { setEventsRefetchMs(eventListRefetchInterval(selectedEvent)); }, [selectedEvent]);
  useEffect(() => {
    if (shouldRevalidateEvents({ event: selectedEvent, dataUpdatedAt: eventsUpdatedAt, nowMs: Date.now() })) refetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent?.id]);
  const {
    parsed, status: geometryStatus, meta: pathMetaState, refetch: refetchGeometry
  } = useEventGeometry(selectedEvent);
  // Exposure checks ALL preferred commodities (not just the one picked in the selector).
  const clockMs = useMemo(() => timelineEpochMs(startDate, currentHour), [startDate, currentHour]);
  const pathUpdatedMs = pathUpdatedMsOf(parsed);
  // The selected hazard goes to BiQ as page context (its own slot, so the page's extras are never overwritten).
  const { setPageHazard } = useBiqPageContext() || {};
  const classWords = resolveStormClass({ text: selectedEvent?.severityData?.text, parsed, lng: selectedEvent?.lng });
  useEffect(() => {
    if (!setPageHazard) return undefined;
    setPageHazard(selectedEvent ? hazardBiqSummary({ event: selectedEvent, classWords, status: geometryStatus }) : null);
    return () => setPageHazard(null);
  }, [setPageHazard, selectedEvent, classWords, geometryStatus]);
  const selectedIsCurrent = !!selectedEvent?.isCurrent;
  const clockState = useMemo(() => {
    if (!parsed || parsed.eventType !== 'TC') return null;
    return selectedIsCurrent ? stormStateAt(parsed, clockMs) : staticStormState(parsed);
  }, [parsed, clockMs, selectedIsCurrent]);
  // Free rectangle of the map (outside the filter strip, legends and the storm card) for fits.
  const { insets, setWrapperEl, setTopEl, setBottomEl, setPanelEl } = useMapInsets();
  const [fitNonce, setFitNonce] = useState(0);
  const handleSelectHazard = useCallback((e) => {
    selectEvent(e);
    setFitNonce((n) => n + 1);
  }, [selectEvent]);
  const hazardDetail = useMemo(() => (selectedEvent ? {
    parsed, status: geometryStatus, clockState, clockMs, pathUpdatedMs, pathMeta: pathMetaState, onRetry: refetchGeometry,
  } : null), [selectedEvent, parsed, geometryStatus, clockState, clockMs, pathUpdatedMs, pathMetaState, refetchGeometry]);
  // The storm card shares the left edge with the shipment card and the hub panel: only one of them is open at a time.
  useEffect(() => {
    if (!selectedEvent) return;
    if (selectedShipmentProp) onShipmentSelect(null);
    if (hubPanel) setHubPanel(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent?.id]);
  useEffect(() => {
    if (selectedEvent && (selectedShipmentProp || hubPanel)) clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShipmentProp, hubPanel]);
  const hazardFitBounds = useMemo(() => (parsed ? hazardBounds(parsed) : null), [parsed]);
  const stormPinBounds = useMemo(() => shiftedStormBounds(parsed), [parsed]);
  const hazardLabels = useMemo(() => ({
    shortClasses: {
      HU: t('map.hazard.shortClasses.HU', 'H'),
      TS: t('map.hazard.shortClasses.TS', 'TS'),
      TD: t('map.hazard.shortClasses.TD', 'TD'),
    },
  }), [t]);


  // Portrait/mobile (e.g. signage panel, <1024px): center on North America so
  // the tall frame frames the Americas well. Landscape/desktop and portrait
  // *monitors* (>=1024px) keep their own center. Ported from
  // portrait-signage 18a161b + d1e391d's council-review width gate.
  //
  // Both centres were raised and moved east for the review: the filter column
  // floats over the map's top-left, and at the old centres it sat on top of
  // North America. Raising the latitude drops the landmasses below that
  // column; moving the longitude east slides North America toward the left
  // edge, which is where the design wants it.
  //
  //   desktop  [15, -60]  -> [26, -48] -> [44, -45.5]
  //   portrait [40, -100] -> [48, -92]
  //
  // The desktop pair was set from a screenshot of the wanted view rather than
  // by taste: pins that appear in both the old and the wanted framing fix the
  // centre at 44 / -45.5, with the longitude agreeing to a tenth of a degree
  // across four of them. It clears the filter column, which with the chip row
  // and the Arrivals/Season row stands ~146px tall and used to cover Canada.
  //
  // The cost is the southern hemisphere: on a ~900px-tall map this puts the
  // bottom edge near -27 degrees, so the Cape growing regions, southern
  // South America and most of Australia start off-screen until the user pans.
  // That is the accepted trade — the top-left is where the eye lands — but it
  // is the thing to revisit first if southern coverage starts to matter.
  // Lowering the latitude is the whole fix; nothing else encodes the view.
  //
  // One centre serves every layer — nothing here reads `layerMode` — so the
  // placement applies to All, Shipments, Weather and Growing alike. Tune these
  // two pairs; nothing else encodes the default view.
  //
  // Tracked as state (not read once) and kept in sync with a matchMedia
  // listener so rotating/resizing an already-mounted device re-centers the
  // map — `MapContainer`'s `center` prop only applies on initial mount, so
  // `MapCenterHandler` below calls `setView` whenever `mapCenter` changes.
  const [isPortraitViewport, setIsPortraitViewport] = useState(
    () => typeof window !== 'undefined'
      && !!window.matchMedia?.('(orientation: portrait) and (max-width: 1023px)').matches
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia('(orientation: portrait) and (max-width: 1023px)');
    const handler = (e) => setIsPortraitViewport(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const mapCenter = useMemo(
    () => (isPortraitViewport ? [48, -92] : [44, -45.5]),
    [isPortraitViewport]
  );

  return (
    <div className="relative w-full flex h-full flex-col">
      {/* Weather controls bar — only when weather visible.
          Commodity filter lives in GlobalFilterStrip (above ScorecardsBar) on
          both breakpoints; we hide it here to avoid duplication. Status
          filter lives there too on desktop only — on mobile it moved into
          this component's own "Map controls" panel below. */}
      {/* Mobile (<lg): "Map controls" content — the LayerToggle (what to
          display: All/Shipments/Weather) plus, when weather is visible, an
          app-styled MapSelect for the weather layer, the Status filter
          (moved here from GlobalFilterStrip's top strip on mobile only —
          see GlobalFilterStrip's `hidden lg:block` gate around its own
          StatusDropdown), and a bordered EventsDropdown (cyclone/flood/
          earthquake as a checklist). Expand/collapse is driven by the "Map
          controls" pill in ScorecardsBar (Option B) via the
          `mobileControlsExpanded` prop lifted to MapPage — no in-place
          header toggle here. Gated ONLY on `mobileControlsExpanded` (not
          `showWeather`) so the LayerToggle stays reachable in Shipments
          mode, when weather is off. `relative isolate z-[1200]` elevates
          this block's own stacking context above the Leaflet map sibling
          below (map is `z-0 isolate`, so a menu z-index alone can't beat
          it) — same pattern as the scorecards unit dropdown (z-[1250]). */}
      {mobileControlsExpanded && (
        <div className="relative isolate z-[1200] mx-2 my-1 lg:hidden">
          <div className="pb-1.5">
            <LayerToggle variant="inline" layerMode={layerMode} onModeChange={onLayerModeChange} />
          </div>
          {showWeather && (
            <div className="flex flex-wrap items-center gap-2 pb-1.5">
              <MapSelect
                value={activeLayer}
                options={WEATHER_LAYERS.map((l) => ({ value: l.id, label: t(`map.layers.${l.id}`, l.label) }))}
                onSelect={onLayerChange}
                ariaLabel={t('map.controls.weatherLayerAria', 'Map weather layer')}
                className="flex-1 min-w-[110px]"
              />
              <StatusDropdown
                regions={regions}
                visibleStatuses={visibleStatuses}
                onStatusesChange={onStatusesChange}
                variant="unified"
              />
              <EventsDropdown activeTypes={activeEventTypes} onToggle={onEventTypeToggle} variant="unified" />
            </div>
          )}
        </div>
      )}

      {/* The weather layer bar (Temp/Precip/… + event toggles) is not part of
          the redesigned map — neither design frame has it, and the space it
          used belongs to the filter pills and arrivals panel. This block was
          always desktop-only (mobile switches layers from the "Map controls"
          panel above), so hiding it leaves mobile untouched — but it does mean
          desktop has no layer switcher until the Weather-mode ticket says where
          one lives. */}
      {showWeather && (
        <div className="hidden">
          <WeatherMapControls
            commodities={commodities}
            regions={regions}
            visibleCommodities={visibleCommodities}
            visibleStatuses={visibleStatuses}
            onCommoditiesChange={() => {}}
            onStatusesChange={() => {}}
            hideFilters
            variant="card"
            countryOptions={[]}
            selectedCountry=""
            onCountryChange={() => {}}
            regionOptions={[]}
            selectedRegion=""
            onRegionChange={() => {}}
            activeLayer={activeLayer}
            onLayerChange={onLayerChange}
            activeEventTypes={activeEventTypes}
            onEventTypeToggle={onEventTypeToggle}
          />
        </div>
      )}

      {/* Map. `z-0 isolate` contains Leaflet's internal z-indexes (panes ~200-700,
          controls ~800-1000) inside this stacking context — the same pattern the
          Home (WeatherMap) and Analytics (ProducingRegionsMap) maps use — so the
          map never paints over the page chrome or the nav drawer. */}
      {/* Fills its column at every breakpoint. The fixed `70svh` this used to
          carry below lg came from the layout that stacked scorecards, legend
          and timeline UNDER the map and let the page scroll; this redesign
          floats all of that over a full-bleed map, so a fixed height just left
          white space below it with the timeline stranded in the middle. */}
      <div ref={setWrapperEl} className="relative z-0 isolate flex-1 min-h-0">
        {/* Layer rail — floats flush right on both breakpoints; the phone
            design keeps the same rail rather than an inline toggle. */}
        <div>
          <MapLayerRail
            value={layerMode}
            onSelect={onLayerModeChange}
            views={savedViews}
            activeViewId={activeViewId}
            onSelectView={onSelectView}
            onCombine={onCombineSave}
            onUpdateView={onUpdateView}
            onDeleteView={onDeleteView}
          />
        </div>



        {/* Loading indicator */}
        {isLayerLoading && showWeather && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] px-3 py-1.5 rounded-full bg-bloom-green/90 text-white flex items-center gap-2 shadow-lg text-xs">
            <Loader2 size={14} className="animate-spin" />
            <span>Loading {WEATHER_LAYERS.find(l => l.id === activeLayer)?.label || 'layer'}...</span>
          </div>
        )}

        <MapContainer
          center={mapCenter}
          zoom={3}
          className="produce-map-controls"
          style={{ height: '100%', width: '100%', background: isDark ? '#1d1d1d' : '#f3f4f6' }}
          zoomControl={false}
          scrollWheelZoom={false}
          doubleClickZoom={false}
          touchZoom={false}
          boxZoom={false}
        >
          <ShipMarkerPanes />
          <FitHazardOnReady bounds={hazardFitBounds} ready={geometryStatus === 'ready'} selectedId={selectedEvent?.id ?? null} fitNonce={fitNonce} insets={insets} />
          <MapClickHandler onMapClick={clearSelection} />
          <RegionHoverLock />
          <ZoomControl position="bottomright" />
          <MapCenterHandler
            center={mapCenter}
            hasLocationTarget={!!(zoomCountry || zoomRegion || focusedRegion)}
          />
          <FocusShipmentHandler shipment={visibleSelected} />
          <DisableZoomHandlers />
          <MapResizeHandler />
          <MapViewportHandler onViewportChange={handleViewportChange} />
          <ZoomToClusterHandler cluster={zoomToCluster} />
          <ResetViewHandler seq={resetViewSeq} center={mapCenter} zoom={3} />
          <ShipClickResolver
            rows={showShipments ? stackedMarkers : []}
            modeRows={showShipments ? modeHoverRows : null}
            onModeHoverChange={setHoverModeKey}
            onModeHit={(key) => {
              const hit = [...airItems, ...truckItems].find((i) => i.key === key);
              if (hit) selectModeRow(hit.row);
            }}
            onHoverChange={handleShipHover}
            onHit={(row) => {
              const d = resolvePinHit(row.stack);
              // A hub box selects its shipment exactly like a vessel does.
              // resolvePinHit routes hubs to the waiting panel, which belongs
              // to the old in-map layout; this redesign has one detail
              // surface — MapPage's ShipmentDetailsPanel — and never mounts
              // that panel, so a 'panel' decision here showed nothing at all.
              if (d.kind === 'panel') {
                const hubItem = (row.stack || []).find((i) => isHubWaiting(i?.shipment));
                if (hubItem?.shipment) {
                  commit({ kind: 'card', shipment: hubItem.shipment, keepHover: true });
                  return;
                }
              }
              commit(d.kind === 'card' ? { ...d, keepHover: true } : d);
            }}
            onMiss={() => commit({ kind: 'miss' })}
          />
          <RegionZoomHandler regions={regions} country={zoomCountry} region={zoomRegion} />

          <TileLayer
            url={isDark ? CARTO_DARK_URL : CARTO_LIGHT_URL}
            attribution='&copy; <a href="https://carto.com/">CARTO</a>'
          />

          {/* Weather tile layer — always mounted, visibility controlled via prop */}
          {hasApiKey && (
            <AnimatedWeatherLayer
              activeLayer={activeLayer}
              currentHour={currentHour}
              startDate={startDate}
              apiMode={API_MODE}
              maxHours={maxHours}
              isDark={isDark}
              opacity={weatherOpacity}
              visible={showWeather}
              isPlaying={isPlaying}
              onZoomStart={handleZoomStart}
              onTilesLoaded={handleTilesLoaded}
              onLayerLoading={handleLayerLoading}
              onLayerReady={handleLayerReady}
            />
          )}

          <HazardGeometryLayer
            parsed={parsed}
            clockState={clockState}
            clockMs={clockMs}
            isPlaying={isPlaying}
            playSpeed={playSpeed}
            isDark={isDark}
            isCurrent={selectedIsCurrent}
            scrubRevision={scrubRevision}
            labels={hazardLabels}
          />

          {/* Region markers. The locations of one administrative region are
              drawn as a single pin carrying their count; clicking it opens the
              overview rather than a popup, since there is no one location to
              describe. A region holding a single location is just that
              location, so it keeps the normal pin — including its marker ref,
              which the Harvest Alerts cards use to open a popup from the side
              panel. */}
          {showRegions && regionClusters.map((cluster) => {
            // "Expand" draws a cluster's members in place, at the zoom the
            // user is already on — the alternative, zooming until the pins
            // separate, moves the rest of the map away to do it. `every`
            // rather than `some`: if a zoom has merged an expanded group with
            // an untouched one, the safe reading is that the new group was
            // never expanded.
            const expanded = cluster.total > 1
              && cluster.regions.every((r) => expandedRegionIds.has(regionMarkerId(r)));
            if (cluster.total > 1 && !expanded) {
              return (
                <Marker
                  key={cluster.key}
                  position={cluster.position}
                  icon={getClusterIcon(cluster.counts, cluster.total)}
                  zIndexOffset={2000}
                  eventHandlers={{ click: () => setOpenCluster(cluster) }}
                />
              );
            }
            // A group of one, or an expanded group: either way every member
            // gets its own pin.
            return cluster.regions.map((region) => {
            const enlarged = hoveredRegionId != null && regionMarkerId(region) === hoveredRegionId;
            return (
            <Marker
              key={`region-${region.commodityKey}-${region.specific_location || region.region}-${cluster.key}`}
              position={regionPinPosition(region, stormPinBounds)}
              icon={getRegionPinIcon(region.harvest_status, layerMode === LAYER_MODES.ALL, enlarged)}
              zIndexOffset={enlarged ? 1000 : 0}
              ref={(m) => {
                const id = regionMarkerId(region);
                if (m) regionMarkerRefs.current.set(id, m);
                else regionMarkerRefs.current.delete(id);
              }}
              eventHandlers={{ click: (e) => e.target.openPopup() }}
            >
              <Popup
                autoPanPaddingTopLeft={POPUP_PAN_TOP_LEFT}
                autoPanPaddingBottomRight={POPUP_PAN_BOTTOM_RIGHT}
              >
                <RegionPopupContent region={region} isDark={isDark} />
              </Popup>
            </Marker>
            );
            });
          })}

          {/* After the pins, so their position effects run first and focus reads the drawn position. */}
          <FocusRegionHandler focusedRegion={focusedRegion} markerRefs={regionMarkerRefs} />

          {/* Weather event markers: own pane above the ship panes (and below popups) */}
          <Pane name="hazards" style={{ zIndex: 695 }}>
            {showWeather && filteredEvents.map((event, idx) => {
              const icon = getHazardIcon(event.type);
              if (!icon) return null;
              return (
                <HazardEventMarker
                  key={`event-${event.id || idx}`}
                  event={event}
                  icon={icon}
                  title={t(`map.events.${event.type}`, event.type)}
                  onSelect={handleSelectHazard}
                />
              );
            })}
          </Pane>

          {/* Ship markers */}
          {showShipments && stackedMarkers.map((row) => {
            const selectedId = String(visibleSelected?.container_number || '').trim();
            const hoveredId = String(hoveredStackId || '').trim();
            const matchId = (item, id) => id && String(item.shipment?.container_number || '').trim() === id;
            // Map hover scales and glows the marker (see ShipMarker); it is
            // deliberately not part of `focusItem`, which draws selection rings.
            const mapHoverId = String(hoveredShipment?.container_number || '').trim();
            const hovered = Boolean(mapHoverId) && row.stack.some((item) => matchId(item, mapHoverId));
            const focusItem = (hoveredId && row.stack.find((item) => matchId(item, hoveredId)))
              || (selectedId && row.stack.find((item) => matchId(item, selectedId)))
              || hubFocusInStack(row.stack, hubPanel)
              || null;
            const topItem = focusItem || { shipment: row.shipment, pin: row.position };
            const topId = String(topItem.shipment?.container_number || '');
            const ring = Boolean(focusItem);
            const topPin = row.position;
            // The selection is a RICHER object than the list row behind this
            // pin: MapPage builds it by merging the search hit over the active
            // row, so it carries voyage fields the row may not. `voyageIsAhead`
            // reads exactly those, so the pin answered "on time" while the
            // route — which is drawn from the selection — answered "ahead",
            // and the same vessel came out blue with a green line behind it.
            // Drawing the selected pin from the selection settles it: pin,
            // route and details panel now read one object.
            const topShipment = selectedId && topId.trim() === selectedId && visibleSelected
              ? { ...topItem.shipment, ...visibleSelected }
              : topItem.shipment;
            // One expression decides the pin colour, the route colour and the
            // details badge, from one object. Anything less and they drift.
            const topStatus = shipmentStatusKey(topShipment);
            const topDelayed = topStatus === 'delayed';
            const topAhead = topStatus === 'ahead';
            return (
              <ShipMarker
                key={row.key}
                shipment={topShipment}
                statusKey={topStatus}
                position={topPin}
                isSelected={ring}
                isHovered={hovered}
                interactive={false}
                zIndexOffset={
                  topDelayed
                    ? 1200000
                    : topAhead
                      ? 400000
                      : ring
                        ? 200000
                        : 80000
                }
              />
            );
          })}

          {/* Air + truck layers. They follow the same showShipments
              toggle as vessels — the control is "shipments", not "sea". */}
          {showShipments && (
            <>
              <ModeShipmentLayer
                items={airItems}
                mode="air"
                selectedKey={selectedModeKey}
                hoverKey={hoverModeKey}
                onSelect={selectModeRow}
              />
              <ModeShipmentLayer
                items={truckItems}
                mode="truck"
                selectedKey={selectedModeKey}
                hoverKey={hoverModeKey}
                onSelect={selectModeRow}
              />
            </>
          )}

          {/* Route for selected shipment */}
          {showShipments && visibleSelected && (visibleSelected.route_geojson || visibleSelected.voyage_stops) && (
            <RouteLayer
              key={`route-${visibleSelected.container_number}-${shipmentStatusKey(visibleSelected)}`}
              shipment={visibleSelected}
              routeGeojson={visibleSelected.route_geojson}
              departurePort={visibleSelected.departure_port}
              arrivalPort={visibleSelected.arrival_port}
              voyageStops={visibleSelected.voyage_stops}
              hasShipsgoCurrent={visibleSelected.has_shipsgo_current}
              vessel={visibleSelected.vessel}
              currentVessel={visibleSelected.current_vessel}
              delayDays={visibleSelected.delay_days}
              rowIsDelayed={voyageHasDelay(visibleSelected)}
              statusColor={routeColorFor(visibleSelected)}
            />
          )}

          {/* Hover preview: path only */}
          {showShipments && hoverPreviewShipment && (
            <RouteLayer
              key={`hover-route-${hoverPreviewShipment.container_number}-${shipmentStatusKey(hoverPreviewShipment)}`}
              preview
              shipment={hoverPreviewShipment}
              routeGeojson={hoverPreviewShipment.route_geojson}
              departurePort={hoverPreviewShipment.departure_port}
              arrivalPort={hoverPreviewShipment.arrival_port}
              voyageStops={hoverPreviewShipment.voyage_stops}
              hasShipsgoCurrent={hoverPreviewShipment.has_shipsgo_current}
              vessel={hoverPreviewShipment.vessel}
              currentVessel={hoverPreviewShipment.current_vessel}
              delayDays={hoverPreviewShipment.delay_days}
              rowIsDelayed={voyageHasDelay(hoverPreviewShipment)}
              // Same palette as the selected route, so the line a hover draws
              // matches the colour of the marker being hovered rather than
              // falling back to the old delayed/on-time green pair.
              statusColor={routeColorFor(hoverPreviewShipment)}
            />
          )}
        </MapContainer>

        {selectedEvent && (
          <HazardCard
            event={selectedEvent}
            isDark={isDark}
            detail={hazardDetail}
            onClose={clearSelection}
            rootRef={setPanelEl}
          />
        )}

        {/* Legends — overlaid on the map (spec: "now located on a MAP, white
            panels a bit transparent") instead of a boxed row below it, which is
            what buys the map its extra height. `bottom-[26px]` clears Leaflet's
            attribution strip in the bottom-right corner. The wrapper is
            `pointer-events-none` so map dragging still works in the gap between
            the two panels, and sits at z-[900] so the draggable ShipmentCard
            (z-[1000]) stays on top. Below lg the two panels stack left-aligned;
            from lg they split to the two bottom corners as in the design. */}
        {layerMode === LAYER_MODES.WEATHER && showWeather && (
          <div ref={setBottomEl} className={`pointer-events-none absolute inset-x-[12px] bottom-[112px] z-[900] ${selectedEvent ? 'max-sm:invisible' : ''} flex flex-col items-start gap-[8px] lg:inset-x-[24px] lg:bottom-[96px] lg:flex-row lg:items-end lg:justify-end`}>
            {/* Hazard pills and the region-status key share this corner —
                weather draws region pins too, so both keys apply. They are in
                one stack for the reason given below: two absolutely placed
                panels in the same corner cannot know each other's size.
                The layer scale is no longer here: the design attaches it to
                the bottom edge beside the timeline, so it moved down there. */}
            <div className="pointer-events-auto flex max-w-full flex-col items-start gap-[8px] lg:max-w-[60%] lg:items-end">
              {/* Only when the key has something to say: with no active hazard
                  type and no shipments the overlay renders nothing, and a pill
                  opening onto an empty panel is worse than no pill. */}
              {(activeEventTypes.length > 0 || showShipments) && (
                <LegendCollapse label={t('map.legend.hazards', 'Hazards')} className="lg:items-end">
                  <StatusLegend
                    activeEventTypes={showWeather ? activeEventTypes : []}
                    showStatuses={false}
                    showInTransit={showShipments}
                    variant="overlay"
                  />
                </LegendCollapse>
              )}
            </div>
          </div>
        )}

        {/* Map keys, bottom-LEFT on both breakpoints. They used to sit bottom
            -right on desktop, where they overlapped the layer rail; the design
            moves them to the left, 24px in, clear of it. Each
            one keys a layer, so each appears exactly when its layer is drawn —
            which means All, drawing both, shows both side by side. They share a
            single container rather than being positioned apart: two absolutely
            placed legends would have to agree on each other's widths to avoid
            overlapping, and would silently stop agreeing the moment either
            gained a row. Review: "when have space put it in the bottom", so
            every layer keys from the same corner at the same height whenever
            the room is there — `legendBottomFor` works out when that is.
            Weather used to keep its own copy in the right-hand stack; the
            review moved it here so every layer keys its pins from the same
            corner. */}
        {(showRegions || showShipments) && (
          <div
            // Stacked, not side by side: two pills abreast in the corner read
            // as one wide object and the review called the placement off.
            // The column is anchored by `bottom`, so it grows upward — Mode &
            // status keeps the corner and Region status sits above it, in DOM
            // order.
            className={`pointer-events-none absolute left-[12px] z-[900] flex flex-col items-start gap-[8px] lg:left-[24px] ${
              legendBottomClass
            }`}
          >
            {/* With both layers drawn there is one combined key, per the new
                All-mode design: two keys abreast read as a single wide object
                and neither said what the other covered. It also drops the
                desktop-only gate the pair needed — one 232px panel fits a
                phone where two stacked keys did not.

                A layer on its own keeps its own key; only All (and a saved
                view holding both) has two things to explain at once. */}
            {showRegions && showShipments ? (
              <div className="pointer-events-auto">
                <MapLegend />
              </div>
            ) : (
              <>
                {/* Region status: what the region pin colours mean. */}
                {showRegions && (
                  // The responsive hide goes on a wrapper, not on the legend
                  // itself: `lg:block` would land on the component's own root
                  // and override its `flex`, silently dropping the gap between
                  // its panel and its toggle.
                  <div className="pointer-events-auto">
                    <RegionStatusLegend />
                  </div>
                )}
                {/* Mode & status: vessels, planes, trucks, hub stacks and what
                    the marker colours mean. */}
                {showShipments && (
                  <div className="pointer-events-auto">
                    <MapModeStatusLegend />
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* The shipment details popup is mounted by MapPage, in the same
            overlay column as the chips and the Arrivals pill — that column is
            what puts it 16px below the pill and bounds its height to 24px
            above the map's bottom edge. Rendering it here would have to
            re-derive both offsets from inside the map. */}

        {/* Inside the map wrapper, not beside it. That wrapper is
            `z-0 isolate`, so a sibling at z-[1040] paints above everything
            within it no matter how high the inner z goes — which put this
            row over the layer rail's combine/edit popover and made the
            popover unclickable. As a child it shares the rail's stacking
            context, where the rail's own z-[1200]-while-open does the job
            it was written for. Position is unchanged: the wrapper and the
            column share a bottom edge and a width at every breakpoint. */}
        {/* Weather timeline + layer scale — one bottom row, both attached to the
            bottom edge, scale to the right of the timeline per the design.
            They share a flex row rather than being placed independently so the
            scale cannot overlap the timeline when the viewport narrows: the row
            shrinks the timeline instead. Below lg the scale is dropped rather
            than stacked — the two together would take most of a phone's map.

            `items-stretch` is what makes the scale exactly as tall as the
            timeline: both dock on the bottom edge, and matching their heights by
            hand would mean re-tuning the scale whenever the timeline's content
            changes. The scale centres its own content in the height it is
            given. */}
        {showWeather && hasApiKey && (
          <div className="pointer-events-none absolute z-[1040] flex items-stretch gap-[12px] max-lg:inset-x-0 max-lg:bottom-0 max-lg:flex-col-reverse max-lg:gap-[8px] lg:inset-x-[24px] lg:bottom-0 lg:justify-center">
            {/* `[&>div]:h-full` makes the timeline card fill the stretched row
                too — without it only the scale filled, and the timeline sat at
                its natural height with a gap beneath it. */}
            <div className="pointer-events-auto min-w-0 flex-1 lg:max-w-[min(900px,calc(100%-360px))] [&>div]:h-full">
              <WeatherMapTimeline
                variant="card"
                speedLabels={TIMELINE_SPEEDS}
                currentHour={currentHour}
                isPlaying={isPlaying}
                isLoadingTiles={isPausedForZoom}
                playSpeed={playSpeed}
                onPlayPause={handlePlayPause}
                onTimeChange={handleScrub}
                onSpeedChange={setPlaySpeed}
                startDate={startDate}
                apiMode={API_MODE}
                maxHours={maxHours}
              />
            </div>
            {/* Weather only. `showWeather` is true on All as well, where the
                forecast is one layer among shipments and regions rather than
                the subject — so the temperature scale, which keys only the
                tiles, is dropped there. The timeline stays: it scrubs the
                forecast on both layers. */}
            {layerMode === LAYER_MODES.WEATHER && (
              /* Two mounts: the treatments differ in markup, not just size.
                 Phones get the one-line `pill` floating above the timeline —
                 `flex-col-reverse` on the row puts these last children on top,
                 so DOM order keeps the timeline primary for assistive tech
                 while the design's stacking is kept visually. The row runs
                 edge to edge on phones (the timeline is full-bleed there), so
                 the pill insets itself to the 16px gutter and stays `w-fit`
                 rather than stretching into a band. From lg the docked `card`
                 sits beside the timeline as before. */
              <>
                <div className="pointer-events-auto shrink-0 max-lg:mx-[16px] lg:hidden">
                  <LegendCollapse label={t('map.legend.scale', 'Scale')}>
                    <WeatherLayerLegend activeLayer={activeLayer} isDark={isDark} variant="pill" center={viewCenter} />
                  </LegendCollapse>
                </div>
                <div className="pointer-events-auto hidden shrink-0 lg:block lg:self-end">
                  <LegendCollapse label={t('map.legend.scale', 'Scale')}>
                    <div className="lg:w-[320px]">
                      <WeatherLayerLegend activeLayer={activeLayer} isDark={isDark} variant="card" center={viewCenter} />
                    </div>
                  </LegendCollapse>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Cluster overview ("N growing regions"). Sibling of the map div for the
          same reason as the stack picker below: that div is `z-0 isolate`, so
          anything inside it can never rise above MapPage's overlay column. */}
      {/* Stack picker ("N ships at this point"). Deliberately a sibling of the
          map div above, not a child: that div is `z-0 isolate`, so anything
          inside it is painted at z-0 whatever its own z-index and can never
          rise above MapPage's z-[1100] overlay column — the picker opened
          underneath the filter chips and the Arrivals pill it overlaps.
          `left-[52px]` clears Leaflet's zoom control (12px margin + 28px
          buttons + 12px). */}
        {showShipments && visibleStackPick && visibleStackPick.length > 1 && (
          <div
            className={`absolute top-[52px] left-[52px] z-[1150] w-[280px] max-h-[320px] overflow-y-auto rounded-lg shadow-lg border text-xs ${
            isDark ? 'bg-[#1d1d1d] border-[#333] text-gray-200' : 'bg-white border-[#e5e7eb] text-[#1f2937]'
          }`}>
            <div className={`px-2.5 py-1.5 font-semibold border-b flex items-center justify-between gap-2 ${isDark ? 'border-[#333]' : 'border-[#e5e7eb]'}`}>
              <span className="min-w-0">
                {hubOverview.groups > 0 ? (
                  <>
                    {t('seaTracking.card.hubWaitingAt', { port: hubOverview.port })}
                    <span className={`block font-normal ${isDark ? 'text-gray-400' : 'text-[#6b7280]'}`}>
                      {t('seaTracking.card.hubSummary', { boxes: hubOverview.totalBoxes.toLocaleString(), groups: hubOverview.groups })}
                      {hubOverview.earliestDepartureLabel && t('seaTracking.card.hubSummaryNext', { date: hubOverview.earliestDepartureLabel })}
                      {hubOverview.overdueCount > 0 && t('seaTracking.card.hubSummaryOverdue', { count: hubOverview.overdueCount })}
                    </span>
                  </>
                ) : (() => {
                  const keys = visibleStackPick.map((item) => String(
                    item.shipment?.current_vessel || item.shipment?.vessel || '',
                  ).toUpperCase().trim());
                  const sameVessel = keys.length > 0 && keys.every((k) => k && k === keys[0]);
                  return sameVessel
                    ? t('seaTracking.card.stackShipments', '{{count}} shipments at this point', { count: visibleStackPick.length })
                    : t('seaTracking.card.stackShips', '{{count}} ships at this point', { count: visibleStackPick.length });
                })()}
              </span>
              <button
                type="button"
                aria-label={t('common.close')}
                onClick={() => { setHoveredStackId(null); setStackPick(null); }}
                className={`shrink-0 text-base leading-none px-1 rounded ${isDark ? 'text-gray-300 hover:bg-[#333]' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                ×
              </button>
            </div>
            {(() => {
            const renderRow = (item, hubRow = null) => {
              const s = item.shipment;
              const delayed = voyageHasDelay(s);
              const ahead = !delayed && voyageIsAhead(s);
              const id = String(s.container_number || '');
              const isHover = Boolean(hoveredStackId) && String(hoveredStackId) === id;
              const isSel = isHover || (
                !hoveredStackId
                && visibleSelected?.container_number
                && String(visibleSelected.container_number) === id
              );
              const rowBg = delayed
                ? (isDark ? 'bg-red-950/50' : 'bg-red-50')
                : ahead
                  ? (isDark ? 'bg-green-950/50' : 'bg-green-50')
                  : (isDark ? 'bg-[#1d1d1d]' : 'bg-white');
              const hoverBg = delayed
                ? (isDark
                  ? 'hover:bg-red-900 hover:ring-2 hover:ring-inset hover:ring-red-400 active:bg-red-800'
                  : 'hover:bg-red-200 hover:ring-2 hover:ring-inset hover:ring-red-600 active:bg-red-300')
                : ahead
                  ? (isDark
                    ? 'hover:bg-green-900 hover:ring-2 hover:ring-inset hover:ring-green-400 active:bg-green-800'
                    : 'hover:bg-green-200 hover:ring-2 hover:ring-inset hover:ring-green-700 active:bg-green-300')
                  : (isDark
                    ? 'hover:bg-[#3a3a3a] hover:ring-2 hover:ring-inset hover:ring-gray-300 active:bg-[#4a4a4a]'
                    : 'hover:bg-gray-200 hover:ring-2 hover:ring-inset hover:ring-gray-800 active:bg-gray-300');
              const selBg = isSel
                ? (isDark ? 'ring-2 ring-inset ring-white bg-[#333]' : 'ring-2 ring-inset ring-gray-900 bg-gray-100')
                : '';
              const titleCls = delayed
                ? (isDark ? 'text-red-400' : 'text-red-700')
                : ahead
                  ? (isDark ? 'text-green-400' : 'text-green-700')
                  : (isDark ? 'text-gray-100' : 'text-[#111827]');
              const boxes = Number(s.total_containers);
              const boxLabel = Number.isFinite(boxes) && boxes > 0
                ? ` · ${boxes} box${boxes === 1 ? '' : 'es'}`
                : '';
              return (
                <button
                  key={s.container_number}
                  type="button"
                  onMouseEnter={() => setHoveredStackId(s.container_number)}
                  onFocus={() => setHoveredStackId(s.container_number)}
                  onClick={() => {
                    setHoveredStackId(null);
                    setStackPick(null);
                   
                    onShipmentSelect(s);
                  }}
                  className={`w-full text-left px-2.5 py-1.5 ${rowBg} ${hoverBg} ${selBg}`}
                >
                  <div className={`font-semibold truncate ${titleCls}`}>{hubRow ? (hubRow.destination || '—') : (currentVesselName(s) || s.container_number)}</div>
                  <div className={isDark ? 'text-gray-400' : 'text-[#6b7280]'}>
                    {hubRow ? t('seaTracking.card.hubRowSubtitle', {
                      boxes: hubRow.boxes,
                      vessel: hubRow.nextVessel || t('seaTracking.card.toBeConfirmed'),
                      date: hubRow.departureLabel || t('seaTracking.card.toBeConfirmed'),
                    }) : <>{s.arrival_port || '—'}{boxLabel}</>}
                  </div>
                  {hubRow?.overdue && (
                    <div className={`font-semibold ${isDark ? 'text-amber-400' : 'text-[#B45309]'}`}>{t('seaTracking.card.hubOverdueMarker')}</div>
                  )}
                </button>
              );
            };
            const sectionTitle = (text) => (
              <div className={`px-2.5 py-1 text-[0.625rem] font-semibold uppercase tracking-wide ${isDark ? 'bg-[#282a29] text-gray-400' : 'bg-[#f3f4f6] text-[#6b7280]'}`}>{text}</div>
            );
            if (hubOverview.groups === 0) return visibleStackPick.map((item) => renderRow(item));
            return (
              <>
                {hubOverview.rows.map((row) => renderRow({ shipment: row.shipment }, row))}
                {hubOverview.others.length > 0 && sectionTitle(t('seaTracking.card.inTransit'))}
                {hubOverview.others.map((shipment) => renderRow({ shipment }))}
              </>
            );
            })()}
          </div>
        )}

    </div>
  );
};

export default UnifiedMapContainer;
